import React, { useEffect, useState, useRef } from 'react';
import { DatabaseIcon, DownloadIcon, RefreshCwIcon, TrashIcon, ClockIcon, CheckCircleIcon, AlertTriangleIcon, RotateCcwIcon, SaveIcon, PlayIcon, LockIcon, UploadIcon, CalendarIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { getBackupConfig, updateBackupConfig, createBackup, getBackupHistory, restoreBackupFromFile, deleteBackup, cleanOldBackups, formatFileSize, BackupConfig as BackupConfigType, BackupHistory } from '../../utils/backupService';
export function BackupConfig() {
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const isAdmin = user?.role === 'administrador';
  const [config, setConfig] = useState<BackupConfigType | null>(null);
  const [history, setHistory] = useState<BackupHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  // Form state
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [retentionDays, setRetentionDays] = useState(30);
  const [backupHour, setBackupHour] = useState(3);
  const [backupMinute, setBackupMinute] = useState(0);
  // Modal states
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<BackupHistory | null>(null);
  // File upload for restore
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [restoreFileError, setRestoreFileError] = useState('');
  useEffect(() => {
    loadData();
  }, []);
  // Note: Auto-backup scheduler is managed by App.tsx globally.
  // No need to start it here - just reload data when config changes.
  const loadData = async () => {
    setLoading(true);
    const [configData, historyData] = await Promise.all([getBackupConfig(), getBackupHistory()]);
    setConfig(configData);
    setEnabled(configData.enabled);
    setFrequency(configData.frequency);
    setRetentionDays(configData.retentionDays);
    setBackupHour(configData.backupHour ?? 3);
    setBackupMinute(configData.backupMinute ?? 0);
    setHistory(historyData);
    setLoading(false);
  };
  const handleSaveConfig = async () => {
    if (!isAdmin) return;
    confirm({
      title: 'Guardar Configuración',
      message: '¿Estás seguro de que deseas guardar la configuración de backups automáticos?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        setSaving(true);
        const currentConfig = config || (await getBackupConfig());
        const success = await updateBackupConfig({
          id: currentConfig.id,
          enabled,
          frequency,
          retentionDays,
          backupHour,
          backupMinute
        });
        if (success) {
          showToast({
            type: 'success',
            title: 'Configuración guardada',
            message: 'La configuración de backup fue guardada correctamente.'
          });
          setSaveMessage('✅ Configuración guardada correctamente');
          await loadData();
        } else {
          showToast({
            type: 'error',
            title: 'Error al guardar',
            message: 'No se pudo guardar la configuración.'
          });
          setSaveMessage('❌ Error al guardar la configuración');
        }
        setTimeout(() => setSaveMessage(''), 3000);
        setSaving(false);
      }
    });
  };
  const handleCreateBackup = async () => {
    if (!user || !isAdmin) return;
    confirm({
      title: 'Crear Backup Manual',
      message: '¿Estás seguro de que deseas crear y descargar un nuevo backup manual ahora?',
      confirmText: 'Crear Backup',
      onConfirm: async () => {
        setCreating(true);
        const result = await createBackup(user.id, user.name, 'manual');
        if (result.success) {
          showToast({
            type: 'success',
            title: 'Backup creado',
            message: 'El backup fue creado y descargado exitosamente.'
          });
          setSaveMessage('✅ Backup creado y descargado exitosamente');
          await loadData();
        } else {
          showToast({
            type: 'error',
            title: 'Error al crear backup',
            message: result.error || 'No se pudo crear el backup.'
          });
          setSaveMessage(`❌ Error: ${result.error}`);
        }
        setTimeout(() => setSaveMessage(''), 3000);
        setCreating(false);
      }
    });
  };
  const handleOpenRestoreModal = () => {
    setRestoreFile(null);
    setRestoreFileError('');
    setIsRestoreModalOpen(true);
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRestoreFileError('');
    const file = e.target.files?.[0];
    if (!file) {
      setRestoreFile(null);
      return;
    }
    if (!file.name.endsWith('.json')) {
      setRestoreFileError('El archivo debe ser un archivo JSON (.json)');
      setRestoreFile(null);
      return;
    }
    setRestoreFile(file);
  };
  const handleRestoreFromFile = async () => {
    if (!restoreFile || !user || !isAdmin) return;
    setRestoring(true);
    setRestoreFileError('');
    try {
      const text = await restoreFile.text();
      const backupData = JSON.parse(text);
      if (typeof backupData !== 'object' || backupData === null || Array.isArray(backupData)) {
        throw new Error('El archivo no tiene el formato de backup esperado');
      }
      const result = await restoreBackupFromFile(backupData, user.id, user.name);
      if (result.success) {
        setSaveMessage('✅ Backup restaurado exitosamente. Recargando...');
        setIsRestoreModalOpen(false);
        setTimeout(() => window.location.reload(), 2000);
      } else {
        setRestoreFileError(`Error: ${result.error}`);
      }
    } catch (err: any) {
      setRestoreFileError(err.message || 'Error al leer el archivo de backup');
    }
    setRestoring(false);
  };
  const handleDeleteBackup = async () => {
    if (!selectedBackup || !isAdmin) return;
    const success = await deleteBackup(selectedBackup.id);
    if (success) {
      showToast({
        type: 'error',
        title: 'Backup eliminado',
        message: 'El backup fue eliminado exitosamente.'
      });
      setSaveMessage('✅ Backup eliminado');
      await loadData();
    } else {
      showToast({
        type: 'error',
        title: 'Error al eliminar',
        message: 'No se pudo eliminar el backup.'
      });
      setSaveMessage('❌ Error al eliminar backup');
    }
    setTimeout(() => setSaveMessage(''), 3000);
    setIsDeleteModalOpen(false);
  };
  const handleCleanOldBackups = async () => {
    if (!isAdmin) return;
    confirm({
      title: 'Limpiar Backups Antiguos',
      message: `¿Estás seguro de que deseas eliminar los backups más antiguos de ${retentionDays} días? Esta acción no se puede deshacer.`,
      confirmText: 'Limpiar',
      variant: 'danger',
      onConfirm: async () => {
        const deleted = await cleanOldBackups(retentionDays);
        setSaveMessage(`✅ ${deleted} backup(s) antiguos eliminados`);
        await loadData();
        setTimeout(() => setSaveMessage(''), 3000);
      }
    });
  };
  const frequencyOptions = [{
    value: 'daily',
    label: 'Diario'
  }, {
    value: 'weekly',
    label: 'Semanal'
  }, {
    value: 'monthly',
    label: 'Mensual'
  }];
  if (!isAdmin) {
    return <Card data-id="element-1017" data-mp="3l2pp34vdzzqr">
        <div className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-1018" data-mp="3l27mlqi4spae">
          <LockIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" data-id="element-1019" data-mp="3g5qy2v70s8ws" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-1020" data-mp="3b6edkh3mfdxd">
            Acceso Restringido
          </h3>
          <p data-id="element-1021" data-mp="cnfsude6hoqw">
            Solo los administradores pueden acceder a la configuración de
            backups.
          </p>
        </div>
      </Card>;
  }
  if (loading) {
    return <Card data-id="element-1022" data-mp="2wuxqzxpr2cq">
        <div className="py-12 text-center" data-id="element-1023" data-mp="2wl7db8rat6i">
          <RefreshCwIcon className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-2" data-id="element-1024" data-mp="3u4qmq2uj8nsy" />
          <p className="text-gray-500" data-id="element-1025" data-mp="3u2mi1iot7krm">Cargando configuración...</p>
        </div>
      </Card>;
  }
  return <div className="space-y-6" data-id="element-1026" data-mp="qo0dlf3nlun1">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1027" data-mp="qm5t21wao4jh">
        <div data-id="element-1028" data-mp="y06jtlvt8q3c">
          <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1029" data-mp="y0ptkfbb486a">
            <DatabaseIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-1030" data-mp="1302g0yy9nd8c" />
            Backup Automático
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1031" data-mp="1cvw0r5m52d17">
            Configura backups automáticos con versionado para proteger tus datos
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-1032" data-mp="1gvwpbj2yc4lz">
          <Button variant="outline" leftIcon={<UploadIcon className="w-4 h-4" data-id="element-1034" data-mp="1qnu4f0x2zgpz" />} onClick={handleOpenRestoreModal} data-id="element-1033" data-mp="1lvj1rqayhgtq">
            Restaurar desde Archivo
          </Button>
          <Button leftIcon={<PlayIcon className="w-4 h-4" data-id="element-1036" data-mp="20lqd7m2jpzkx" />} onClick={handleCreateBackup} isLoading={creating} data-id="element-1035" data-mp="1vlvkre34h1xr">
            Crear Backup Ahora
          </Button>
        </div>
      </div>

      {saveMessage && <div className={`p-3 rounded-lg text-sm ${saveMessage.startsWith('✅') ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'}`} data-id="element-1037" data-mp="2dw4arprgafyk">
          {saveMessage}
        </div>}

      {/* Info banner about file-based backups */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800" data-id="element-1038" data-mp="2xik7lgntzo5d">
        <div className="flex items-start gap-3" data-id="element-1039" data-mp="31j42edkxmfoc">
          <DownloadIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-1040" data-mp="36jics2a0a1pj" />
          <div data-id="element-1041" data-mp="3bjmshq33asep">
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200" data-id="element-1042" data-mp="3bi1zw60c5jjh">
              Los backups se descargan automáticamente como archivos JSON
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1" data-id="element-1043" data-mp="1h4twhpyanzm8">
              Guarda los archivos descargados en un lugar seguro. Para
              restaurar, usa el botón "Restaurar desde Archivo" y selecciona el
              archivo JSON del backup.
            </p>
          </div>
        </div>
      </div>

      {/* Configuración */}
      <Card data-id="element-1044" data-mp="zw9h4iy77cdi">
        <div className="p-6 space-y-6" data-id="element-1045" data-mp="ztncar2ozayz">
          <div data-id="element-1046" data-mp="ux6o5wvhswlw">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1047" data-mp="uxpvkjnio2lo">
              Configuración de Backups Automáticos
            </h3>

            <div className="space-y-4" data-id="element-1048" data-mp="1si2ol5ufvf6m">
              {/* Activar/Desactivar */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-1049" data-mp="1nm3xhahq7j2i">
                <div data-id="element-1050" data-mp="1dm4lf3m3lakw">
                  <p className="font-semibold text-gray-900 dark:text-white" data-id="element-1051" data-mp="1dmmp2lb3ar2u">
                    Backups Automáticos
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1052" data-mp="27zh29hz2yp1">
                    Crear backups automáticamente según la frecuencia
                    configurada
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-1053" data-mp="2uftat490g535">
                  <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="sr-only peer" data-id="element-1054" data-mp="2gg0kha1ebqvp" />

                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600" data-id="element-1055" data-mp="26p5npbqix8w0"></div>
                </label>
              </div>

              {/* Frecuencia y Hora */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1056" data-mp="193uonzgelcnt">
                <Select label="Frecuencia de Backup" options={frequencyOptions} value={frequency} onChange={value => setFrequency(value as any)} data-id="element-1057" data-mp="1967fl2452xd0" />

                <Input label="Retención (días)" type="number" value={retentionDays} onChange={e => setRetentionDays(parseInt(e.target.value) || 30)} min={1} max={365} helperText="Backups más antiguos se eliminarán automáticamente" data-id="element-1058" data-mp="1e37mff8tc456" />
              </div>

              {/* Hora programada */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-1059" data-mp="2br92zdyeue9h">
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2" data-id="element-1060" data-mp="26sxr6xok1tom">
                  <ClockIcon className="w-4 h-4 text-blue-500" data-id="element-1061" data-mp="44fw5vgf944g" />
                  Hora del backup automático
                </p>
                <div className="flex items-center gap-3" data-id="element-1062" data-mp="92r6066pvemo">
                  <div className="flex flex-col gap-1" data-id="element-1063" data-mp="dv0xvrre6orl">
                    <label className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1064" data-mp="dxn3vmymww7p">
                      Hora (0–23)
                    </label>
                    <input type="number" min={0} max={23} value={backupHour} onChange={e => setBackupHour(Math.min(23, Math.max(0, parseInt(e.target.value) || 0)))} className="w-20 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1065" data-mp="3hd2kx85fk62c" />
                  </div>
                  <span className="text-gray-400 text-xl font-bold mt-5" data-id="element-1066" data-mp="2r4cwznoemedv">
                    :
                  </span>
                  <div className="flex flex-col gap-1" data-id="element-1067" data-mp="2hcgxc86o6g7j">
                    <label className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1068" data-mp="2hbxf0qd3xpsz">
                      Minuto (0–59)
                    </label>
                    <input type="number" min={0} max={59} value={backupMinute} onChange={e => setBackupMinute(Math.min(59, Math.max(0, parseInt(e.target.value) || 0)))} className="w-20 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono text-center focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1069" data-mp="28br4ndcph5y1" />
                  </div>
                  <div className="mt-5 ml-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-1070" data-mp="1h5tzl58j6u9z">
                    = {String(backupHour).padStart(2, '0')}:
                    {String(backupMinute).padStart(2, '0')} hrs
                  </div>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2" data-id="element-1071" data-mp="2esartw2eqhov">
                  El backup se ejecutará a esta hora según la frecuencia
                  seleccionada.
                </p>
              </div>

              {/* Información del último backup y próximo backup */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1072" data-mp="oetu0pujxig7">
                {config?.lastBackupAt && <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800" data-id="element-1073" data-mp="teohejf4bpwi">
                    <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300" data-id="element-1074" data-mp="37kmh5x4guqfz">
                      <ClockIcon className="w-4 h-4" data-id="element-1075" data-mp="32mkxpb34op3z" />
                      <span className="text-sm font-medium" data-id="element-1076" data-mp="3gmm230ilqeo7">
                        Último backup:{' '}
                        {new Date(config.lastBackupAt).toLocaleString('es-ES')}
                      </span>
                    </div>
                  </div>}
                {config?.enabled && config?.nextBackupAt && <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800" data-id="element-1077" data-mp="2jy67ur4jrh4r">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300" data-id="element-1078" data-mp="2xpul4xqj8bnb">
                      <CalendarIcon className="w-4 h-4" data-id="element-1079" data-mp="2svghy95mp083" />
                      <span className="text-sm font-medium" data-id="element-1080" data-mp="256e47ce47riv">
                        Próximo backup:{' '}
                        {new Date(config.nextBackupAt).toLocaleString('es-ES')}
                      </span>
                    </div>
                  </div>}
              </div>

              {/* Auto-backup note */}
              {enabled && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800" data-id="element-1081" data-mp="2z12wmxunz3rh">
                  <p className="text-xs text-amber-700 dark:text-amber-300" data-id="element-1082" data-mp="341gu5lyn2vb9">
                    <strong data-id="element-1083" data-mp="390k0d9qcv7kt">Nota:</strong> El backup automático se ejecuta una
                    sola vez por el administrador activo, a la hora configurada.
                    Solo se descargará en el navegador del administrador que
                    esté conectado en ese momento. Si no hay sesión activa, el
                    backup se ejecutará en la próxima sesión del administrador.
                  </p>
                </div>}
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-1084" data-mp="kqk8vzxry4a">
            <Button leftIcon={<SaveIcon className="w-4 h-4" data-id="element-1086" data-mp="alj2of4uo8o3" />} onClick={handleSaveConfig} isLoading={saving} data-id="element-1085" data-mp="aoovo5glnup3">
              Guardar Configuración
            </Button>
            <Button variant="outline" leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1088" data-mp="3sujxj4bwgahd" />} onClick={handleCleanOldBackups} data-id="element-1087" data-mp="rshbj40secm">
              Limpiar Backups Antiguos
            </Button>
          </div>
        </div>
      </Card>

      {/* Historial de Backups */}
      <Card data-id="element-1089" data-mp="ohms7v4s3su8">
        <div className="p-6" data-id="element-1090" data-mp="jok80dlw5vqp">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2" data-id="element-1091" data-mp="jnsf5ajoic44">
            <RefreshCwIcon className="w-5 h-5" data-id="element-1092" data-mp="flx95sqszak0" />
            Historial de Backups ({history.length})
          </h3>

          {history.length === 0 ? <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-1093" data-mp="17e66eyufw6xn">
              <DatabaseIcon className="w-12 h-12 mx-auto mb-3 opacity-30" data-id="element-1094" data-mp="17c1tjrekbxl8" />
              <p data-id="element-1095" data-mp="2y0jv3xb8o8ga">No hay backups disponibles</p>
              <p className="text-sm mt-1" data-id="element-1096" data-mp="2txn69ov3vg3w">
                Crea tu primer backup usando el botón superior
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-1097" data-mp="2oyu0kim28tea">
              <table className="w-full" data-id="element-1098" data-mp="2jzh6bcsv0k1o">
                <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-1099" data-mp="2k0s9baehdnrq">
                  <tr data-id="element-1100" data-mp="3hm6ds26bzoux">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1101" data-mp="3cnwizsoiqq9u">
                      Versión
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1102" data-mp="37vbosq1pab5s">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1103" data-mp="48v00u7chmlp">
                      Fecha
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1104" data-mp="6t15j6blxl4d">
                      Tamaño
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1105" data-mp="gqoybqpnkijw">
                      Creado por
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1106" data-mp="lnp4e1mi5lb0">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1107" data-mp="uovpko03jkal">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1108" data-mp="14ickjvbyz5fu">
                  {history.map(backup => <tr key={backup.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-1109" data-mp="1dgg3qkzhfq4h">
                      <td className="px-4 py-3" data-id="element-1110" data-mp="3afhldp1e9d4e">
                        <span className="font-mono text-sm font-semibold text-blue-600 dark:text-blue-400" data-id="element-1111" data-mp="35jae9sx9hvkv">
                          v{backup.versionNumber}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-1112" data-mp="3eidwkuolu6yb">
                        <Badge variant={backup.backupType === 'automatic' ? 'info' : 'default'} data-id="element-1113" data-mp="3t83c5uxg2di4">
                          {backup.backupType === 'automatic' ? 'Automático' : 'Manual'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400" data-id="element-1114" data-mp="27v8vroo3tbdx">
                        {new Date(backup.createdAt).toLocaleString('es-ES')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400" data-id="element-1115" data-mp="1tpqjsnc07ycd">
                        {formatFileSize(backup.fileSize)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400" data-id="element-1116" data-mp="1k06vh25ivca4">
                        {backup.createdByUserName || 'Sistema'}
                      </td>
                      <td className="px-4 py-3" data-id="element-1117" data-mp="1a0pv9091hqlj">
                        {backup.restoredAt ? <Badge variant="success" data-id="element-1118" data-mp="1f0u0fu4hyywg">
                            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-1119" data-mp="1f1lzjexazerh" />
                            Restaurado
                          </Badge> : backup.status === 'completed' ? <Badge variant="success" data-id="element-1120" data-mp="161qhqr98slih">Completado</Badge> : backup.status === 'failed' ? <Badge variant="danger" data-id="element-1121" data-mp="30ddlx4e0xybj">Fallido</Badge> : <Badge variant="warning" data-id="element-1122" data-mp="10bdzkxonvqv4">En progreso</Badge>}
                      </td>
                      <td className="px-4 py-3" data-id="element-1123" data-mp="vbt08m5kxghv">
                        <div className="flex items-center justify-end gap-2" data-id="element-1124" data-mp="va88136qm9n6">
                          <button onClick={() => {
                      setSelectedBackup(backup);
                      setIsDeleteModalOpen(true);
                    }} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Eliminar" data-id="element-1125" data-mp="qbn6n0fr1jx5">
                            <TrashIcon className="w-4 h-4" data-id="element-1126" data-mp="2nnjeahncbnb" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </Card>

      {/* Modal de Restauración desde Archivo */}
      <Modal isOpen={isRestoreModalOpen} onClose={() => !restoring && setIsRestoreModalOpen(false)} title="Restaurar Backup desde Archivo" size="sm" icon={<UploadIcon className="w-5 h-5" data-id="element-1128" data-mp="2ear0hhugmyd0" />} accentColor="amber" data-id="element-1127" data-mp="2599drgmjryfc">
        <div className="space-y-4" data-id="element-1129" data-mp="2j9ttu512vg55">
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800" data-id="element-1130" data-mp="2j8skc4zpn1pj">
            <div className="flex items-start gap-3" data-id="element-1131" data-mp="1mlnrxpvjj1uk">
              <AlertTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" data-id="element-1132" data-mp="1qkmyfewx76o4" />
              <div data-id="element-1133" data-mp="1vjg9l0oqill4">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200" data-id="element-1134" data-mp="1viy3lcc9st00">
                  ⚠️ Advertencia: Esta acción sobrescribirá todos los datos
                  actuales
                </p>
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2" data-id="element-1135" data-mp="12vk87io805i0">
                  Selecciona un archivo JSON de backup previamente descargado.
                  Se recomienda crear un backup manual antes de restaurar.
                </p>
              </div>
            </div>
          </div>

          {/* File upload area */}
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors" onClick={() => fileInputRef.current?.click()} data-id="element-1136" data-mp="2jynxoydmm4ak">
            <input ref={fileInputRef} type="file" accept=".json" onChange={handleFileChange} className="hidden" data-id="element-1137" data-mp="1n7vgfxeyi677" />

            <UploadIcon className="w-8 h-8 mx-auto text-gray-400 mb-2" data-id="element-1138" data-mp="215ad9n7jhg97" />
            {restoreFile ? <div data-id="element-1139" data-mp="3cmti1k0rpr9v">
                <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1140" data-mp="3cmaamx8qula3">
                  {restoreFile.name}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1141" data-mp="37n7418d48700">
                  {formatFileSize(restoreFile.size)}
                </p>
              </div> : <div data-id="element-1142" data-mp="3k6kw8ya05cig">
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1143" data-mp="3k748bycz16ok">
                  Haz clic para seleccionar un archivo JSON de backup
                </p>
                <p className="text-xs text-gray-400 mt-1" data-id="element-1144" data-mp="3b54m8rn3ncer">
                  savidna-backup-vX-YYYY-MM-DD.json
                </p>
              </div>}
          </div>

          {restoreFileError && <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg" data-id="element-1145" data-mp="7p7c2ioxu4gf">
              <p className="text-sm text-red-600 dark:text-red-400" data-id="element-1146" data-mp="3p4mi1qytbpxm">
                {restoreFileError}
              </p>
            </div>}

          <div className="flex justify-end gap-3" data-id="element-1147" data-mp="lop5xch3c7wn">
            <Button variant="outline" onClick={() => setIsRestoreModalOpen(false)} disabled={restoring} data-id="element-1148" data-mp="ln49tif4ogwo">
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleRestoreFromFile} isLoading={restoring} disabled={!restoreFile} leftIcon={<RotateCcwIcon className="w-4 h-4" data-id="element-1150" data-mp="2cts97srx3yoc" />} data-id="element-1149" data-mp="2mk4slhnzxlsw">
              Restaurar Backup
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Eliminación */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Backup" size="sm" icon={<TrashIcon className="w-5 h-5" data-id="element-1152" data-mp="2wgq14cz85f1h" />} accentColor="rose" data-id="element-1151" data-mp="35gd4v5ffewxp">
        <div className="space-y-4" data-id="element-1153" data-mp="2wiuedlj0jqef">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1154" data-mp="2rhwun4icuoat">
            ¿Estás seguro de que deseas eliminar el registro del backup{' '}
            <strong data-id="element-1155" data-mp="1l2cflery1bom">v{selectedBackup?.versionNumber}</strong>?
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500" data-id="element-1156" data-mp="1g289mionvzcn">
            Esto solo elimina el registro del historial. El archivo descargado
            no se verá afectado.
          </p>

          <div className="flex justify-end gap-3 pt-2" data-id="element-1157" data-mp="j0mjl16556s">
            <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)} data-id="element-1158" data-mp="hfsdwirno9g">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDeleteBackup} data-id="element-1159" data-mp="9cnd8wqiri94">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}